import { useQuery } from "@tanstack/react-query";
import fetcher from "src/utils/fetcher";
import { useAuth } from "src/components/utils/auth/assist/authContext";
import { DEFAULT_OWNER_NAME } from "./useSendCards";

interface InkedRecipientAddress {
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface InkedCardSend {
  product: string;
  delivery_id: number;
  campaign_id: number;
  preview_url: string;
  thumbnail_url: string;
  record_id: string;
  recipient_name: string;
  recipient_address: string | null;
}

interface InkedTransaction {
  status: string;
  amount: number;
  transaction_date: string;
  item_count: number;
  card_sends: InkedCardSend[];
  send_at: string;
  thumbnail_url: string;
}

interface InkedUsageResponse {
  total_records: number;
  transactions: InkedTransaction[];
  total_cards_sent: number;
}

export interface InkedUsageParams {
  limit?: number;
  offset?: number;
  dateBegin?: string;
  dateEnd?: string;
  search?: string;
}

export interface CardSend {
  product: string;
  deliveryId: number;
  campaignId: number;
  previewUrl: string;
  thumbnailUrl: string;
  recordId: string;
  recipientName: string;
  recipientAddress: string;
}

export interface Transaction {
  status: string;
  amount: number;
  transactionDate: string;
  itemCount: number;
  cardSends: CardSend[];
  sendAt: string;
  thumbnailUrl: string;
}

export interface InkedUsageData {
  totalRecords: number;
  transactions: Transaction[];
  totalCardsSent: number;
}

export const useInkedUsage = (params: InkedUsageParams = {}) => {
  const { user } = useAuth();
  const { limit = 10, offset = 0, dateBegin, dateEnd, search } = params;

  return useQuery({
    queryKey: ["inkedUsage", user?.email, limit, offset, dateBegin, dateEnd, search],
    queryFn: async (): Promise<InkedUsageData> => {
      const searchParams = new URLSearchParams();
      searchParams.set("limit", limit.toString());
      searchParams.set("offset", offset.toString());
      if (dateBegin) searchParams.set("date_begin", dateBegin);
      if (dateEnd) searchParams.set("date_end", dateEnd);
      if (search) searchParams.set("search", search);

      const response = await fetcher({
        url: `/api/inked/usage?${searchParams.toString()}`,
        method: "GET",
      });

      const data = response.data as InkedUsageResponse;
      return {
        totalRecords: data.total_records,
        transactions: data.transactions.map((t) => ({
          status: t.status,
          amount: t.amount, // Amount in cents, formatted to dollars in UI
          transactionDate: t.transaction_date,
          itemCount: t.item_count,
          sendAt: t.send_at,
          thumbnailUrl: t.thumbnail_url || "", // TODO: Remove fallback once API returns thumbnail_url
          cardSends: t.card_sends.map((cs) => {
            let formattedAddress = "";
            const addrString = cs.recipient_address;

            if (addrString && typeof addrString === "string") {
              try {
                const addr = JSON.parse(addrString) as InkedRecipientAddress;
                const addressParts = [
                  addr.address1,
                  addr.address2,
                  `${addr.city}, ${addr.state} ${addr.postcode}`,
                ].filter(Boolean);
                formattedAddress = addressParts.join(", ");
              } catch {
                formattedAddress = addrString;
              }
            }

            return {
              product: cs.product,
              deliveryId: cs.delivery_id,
              campaignId: cs.campaign_id,
              previewUrl: cs.preview_url,
              thumbnailUrl: cs.thumbnail_url,
              recordId: cs.record_id,
              recipientName: cs.recipient_name || DEFAULT_OWNER_NAME,
              recipientAddress: formattedAddress,
            };
          }),
        })),
        totalCardsSent: data.total_cards_sent,
      };
    },
    enabled: !!user,
    staleTime: 30000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
